// Nombres de promos y regalos vistos en la sesión: sirven cuando Promociones deja de listarlos (pausa).
import { PROMOTION_NAMES_STORAGE_PREFIX } from '../constants';
import type { EvaluateSalePromotionsResponse } from '../api/wizardPromotionsContract';
import { isSureKeepOnly, paymentOptionsChipLabel } from './promotionAppliesTo';

interface RememberedNames {
    promotions: Record<string, string>;
    gifts: Record<string, string>;
    /** Promos vistas que solo aplican con crédito SureKeep. */
    sureKeepOnly: Partial<Record<string, true>>;
    /** Chip de forma de pago de cada promo vista que lo lleva. */
    paymentLabels: Partial<Record<string, string>>;
}

const memory = new Map<string, RememberedNames>();

function load(sessionId: string): RememberedNames {
    const cached = memory.get(sessionId);
    if (cached) return cached;
    let names: RememberedNames = { promotions: {}, gifts: {}, sureKeepOnly: {}, paymentLabels: {} };
    try {
        const raw = sessionStorage.getItem(PROMOTION_NAMES_STORAGE_PREFIX + sessionId);
        if (raw) names = { ...names, ...(JSON.parse(raw) as Partial<RememberedNames>) };
    } catch {
        // Sin almacenamiento solo queda lo que haya en memoria.
    }
    memory.set(sessionId, names);
    return names;
}

/** Guarda los nombres de las promos evaluadas y de sus regalos. */
export function rememberEvaluatedNames(
    sessionId: string,
    evaluation: EvaluateSalePromotionsResponse,
): void {
    const current = load(sessionId);
    const next: RememberedNames = {
        promotions: { ...current.promotions },
        gifts: { ...current.gifts },
        sureKeepOnly: { ...current.sureKeepOnly },
        paymentLabels: { ...current.paymentLabels },
    };
    for (const promo of evaluation.promotions) {
        next.promotions[promo.promotionId] = promo.name;
        if (isSureKeepOnly(promo)) next.sureKeepOnly[promo.promotionId] = true;
        const label = paymentOptionsChipLabel(promo);
        if (label !== null) next.paymentLabels[promo.promotionId] = label;
        for (const option of promo.giftOptions) next.gifts[option.sku] = option.name;
    }
    memory.set(sessionId, next);
    try {
        sessionStorage.setItem(PROMOTION_NAMES_STORAGE_PREFIX + sessionId, JSON.stringify(next));
    } catch {
        // Sin almacenamiento el nombre vive solo en memoria.
    }
}

export function rememberedPromotionName(
    sessionId: string,
    promotionId: string,
): string | undefined {
    return load(sessionId).promotions[promotionId];
}

export function rememberedSureKeepOnly(sessionId: string, promotionId: string): boolean {
    return Boolean(load(sessionId).sureKeepOnly[promotionId]);
}

/** Chip de forma de pago de una promo vista; las guardadas antes del chip caen a «Solo SureKeep». */
export function rememberedPaymentLabel(sessionId: string, promotionId: string): string | null {
    const names = load(sessionId);
    const label = names.paymentLabels[promotionId];
    if (label !== undefined) return label;
    return names.sureKeepOnly[promotionId] ? 'Solo SureKeep' : null;
}

export function rememberedGiftName(sessionId: string, sku: string): string | undefined {
    return load(sessionId).gifts[sku];
}

/** Olvida los nombres en memoria; para pruebas. */
export function resetRememberedNames(): void {
    memory.clear();
}
